import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Map,
  QrCode,
  Trophy,
  Gift,
  Star,
  Award,
  CheckCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';

const TreasureHuntPage = () => {
  const steps = [
    {
      icon: QrCode,
      title: 'Scan Clues',
      description: 'Find QR codes hidden throughout the venue'
    },
    {
      icon: CheckCircle,
      title: 'Complete Tasks',
      description: 'Answer questions and complete challenges'
    },
    {
      icon: Trophy,
      title: 'Win Prizes',
      description: 'Collect points and claim awesome rewards'
    }
  ];

  const rewards = [
    {
      icon: Gift,
      title: 'Exclusive Merch',
      description: 'Limited-edition KNOWN CON swag',
      points: '100 pts'
    },
    {
      icon: Star,
      title: 'VIP Experience',
      description: 'Meet-and-greet with a keynote speaker',
      points: '250 pts'
    },
    {
      icon: Award,
      title: 'Grand Prize',
      description: 'Free all-access pass to next year',
      points: '500 pts'
    },
    {
      icon: Trophy,
      title: 'Daily Winners',
      description: 'Gift cards and special surprises',
      points: 'Varies'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1920&h=1080&fit=crop')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/90 via-[#0B1B3B]/80 to-[#0B1B3B]/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-[#CFA64B]">Treasure</span> Hunt
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Find hidden clues, win rewards, and discover more about the KNOWN CON journey.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            Start the Hunt
          </Button>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Three steps to adventure and rewards</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-10 h-10 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Rewards & Challenges */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Rewards & Challenges</h2>
            <p className="text-xl text-gray-600">Amazing prizes await the adventurous</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {rewards.map((reward, idx) => {
              const Icon = reward.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">{reward.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{reward.description}</p>
                    <div className="inline-block px-3 py-1 bg-[#CFA64B] text-[#0B1B3B] rounded-full text-xs font-bold">
                      {reward.points}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Live Leaderboard */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Live Leaderboard</h2>
            <p className="text-xl text-gray-600">See who's leading the treasure hunt</p>
          </div>

          <Card className="border-2 border-[#CFA64B]">
            <CardContent className="p-12">
              <div className="text-center">
                <Trophy className="w-24 h-24 text-[#CFA64B] mx-auto mb-6" />
                <p className="text-2xl font-bold text-[#0B1B3B] mb-4">Leaderboard Coming Soon</p>
                <p className="text-gray-600">Rankings will be updated in real-time during the event</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rules & Fair Play */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Rules & Fair Play</h2>
            <p className="text-xl text-gray-600">Play with honesty and team spirit</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-10">
              <ul className="space-y-4 text-gray-700 text-lg">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#CFA64B] flex-shrink-0 mt-1" />
                  <span>All clues must be scanned using the official KNOWN CON app</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#CFA64B] flex-shrink-0 mt-1" />
                  <span>Teams are encouraged but not required (max 4 people per team)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#CFA64B] flex-shrink-0 mt-1" />
                  <span>Respect venue boundaries and staff instructions at all times</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#CFA64B] flex-shrink-0 mt-1" />
                  <span>No running, pushing, or unsafe behavior during the hunt</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#CFA64B] flex-shrink-0 mt-1" />
                  <span>Winners will be announced daily at 8 PM on the main stage</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] to-[#CFA64B]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-10">Ready to begin the adventure?</h2>
          <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">Join Now</Button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Attendee Guide</a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">FAQs</a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Contact Support</a>
          </div>
        </div>
      </div>

      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TreasureHuntPage;